import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

// ─── File type color/label helper ─────────────────────────────────────────────

const getFileIconConfig = (mimeType: string) => {
  if (mimeType?.includes('pdf')) return '📄';
  if (mimeType?.includes('image')) return '🖼️';
  if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet') || mimeType?.includes('xls')) return '📊';
  if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
  if (mimeType?.includes('video')) return '🎥';
  return '📎';
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface AttachmentsDialogProps {
  open: boolean;
  onClose: () => void;
  transactionItem: any;
  downloadingFileId: string | null;
  onDownload: (file: any, fileName: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const AttachmentsDialog: React.FC<AttachmentsDialogProps> = ({
  open,
  onClose,
  transactionItem,
  downloadingFileId,
  onDownload,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const files: any[] = transactionItem?.file_attachmet_list || [];

  // Reset selection on open
  useEffect(() => {
    if (open) setSelectedIds(new Set());
  }, [open]);

  const toggleOne = (idx: number) => {
    const next = new Set(selectedIds);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    setSelectedIds(next);
  };

  // ── Reusable mini checkbox ─────────────────────────────────────────────────
  

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
          maxWidth: '400px',
        },
      }}
    >
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 0.8,
          py: 0.8,
          borderBottom: '1px solid #e0e4ea',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
        }}
      >
        <Typography
          sx={{ fontSize: 15, fontWeight: 600, color: '#fff', letterSpacing: '-0.01em' }}
        >
          Attachments ({files.length})
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          
          <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
              title="Close"
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
        </Box>
      </Box>

      {/* ── File list ─────────────────────────────────────────────────────── */}
      <DialogContent sx={{ p: 0, maxHeight: '52vh', overflowY: 'auto' }}>
        {files.length > 0 ? (
          <Box>
            {files.map((file, idx) => {
              const isSelected = selectedIds.has(idx);
              const mimeType = file.mime_type || file.type || '';
              const isImage = mimeType.startsWith('image/');
              const fileName = file.file_name || file.name || `File ${idx + 1}`;
            //   const uploadDate = file.created_at || file.uploadedAt;
            //   const uploadedBy = file.uploaded_by?.firstName
                // ? `${file.uploaded_by.firstName} ${file.uploaded_by.lastName || ''}`.trim()
                // : file.uploadedBy || '';
              const fileId = file._id || file.id;

              return (
                <Box
                  key={fileId || idx}
                  onClick={() => toggleOne(idx)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 0.8,
                    py: 0.8,
                    bgcolor: isSelected ? '#eef3fd' : 'transparent',
                    borderLeft: `3px solid ${isSelected ? '#1b73e8' : 'transparent'}`,
                    borderBottom: '1px solid #f0f2f5',
                    cursor: 'pointer',
                    transition: 'background 0.12s, border-color 0.12s',
                    '&:last-child': { borderBottom: 'none' },
                    '&:hover': { bgcolor: isSelected ? '#eef3fd' : '#f7f9fc' },
                  }}
                >
                  
    
                  {/* File icon / thumbnail */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '6px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#f0f2f5',
                    }}
                  >
                    {isImage && file.url ? (
                      <img
                        src={file.url}
                        alt={fileName}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '20px', lineHeight: 1 }}>
                        {getFileIconConfig(mimeType)}
                      </Typography>
                    )}
                  </Box>

                  {/* File info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      title={fileName}
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 500,
                        color: '#1c2433',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 300,
                        mb: 0.25,
                      }}
                    >
                      {fileName}
                    </Typography>

                    {/* <Typography sx={{ fontSize: 11.5, color: '#7a8699' }}>
                      {uploadDate
                        ? new Date(uploadDate).toLocaleString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                      {uploadedBy && (
                        <>
                          {' '}by{' '}
                          <Box component="span" sx={{ color: '#4a5568', fontWeight: 500 }}>
                            {uploadedBy}
                          </Box>
                        </>
                      )}
                    </Typography> */}
                  </Box>

                  {/* Download button */}
                  {file.file_path && (
                    <IconButton
                      size="small"
                      disabled={downloadingFileId === fileId}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownload(file, fileName);
                      }}
                      title="Download"
                      sx={{
                        color: '#9aa5b4',
                        borderRadius: '6px',
                        flexShrink: 0,
                        transition: 'all 0.15s',
                        '&:hover': { bgcolor: '#e8eef8', color: '#1b73e8' },
                      }}
                    >
                      {downloadingFileId === fileId ? (
                        <CircularProgress size={15} />
                      ) : (
                        <DownloadIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
            }}
          >
            <AttachFileIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
            <Typography color="text.secondary" variant="body2" align="center">
              No attachments found
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AttachmentsDialog;